import { assertSuccessfulEnvelope, isApiEnvelope } from '../../http/envelope';
import { EnterHttpError } from '../../http/errors';
import type { EnterHttpClient, HttpRequestOptions } from '../../http/types';
import type { ApiEnvelope } from '../../types';
export type { ApiModuleDeps } from '../../http/api-module-deps';

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue>;
export type ApiPayload = Record<string, never>;
export type ApiEmpty = null;

/** 删除空查询参数，避免把 undefined、null 或空字符串发给后端。 */
export function cleanQuery<T extends object | undefined>(query: T): T {
    if (!query) {
        return query;
    }

    return Object.fromEntries(
        Object.entries(query).filter(
            ([, value]) => value !== null && value !== undefined && value !== ''
        )
    ) as T;
}

/** 校验标准业务信封；适用于契约明确要求失败即 reject 的领域接口。 */
export async function envelope<T>(promise: Promise<ApiEnvelope<T>>): Promise<ApiEnvelope<T>> {
    const response = await promise;
    if (!isApiEnvelope<T>(response)) {
        throw new EnterHttpError({
            message: 'Invalid API response envelope',
            responseBody: response,
        });
    }

    return assertSuccessfulEnvelope(response);
}

/** 领域 GET 的轻量封装，统一清理 query 并校验响应信封。 */
export function get<T>(
    http: EnterHttpClient,
    path: string,
    query?: object,
    options?: Omit<HttpRequestOptions, 'method' | 'body' | 'query'>
) {
    return envelope<T>(
        http.get<ApiEnvelope<T>>(path, {
            ...options,
            query: cleanQuery(query) as QueryParams | undefined,
        })
    );
}

/** 领域 POST 的轻量封装。 */
export function post<T>(
    http: EnterHttpClient,
    path: string,
    body?: unknown,
    options?: Omit<HttpRequestOptions, 'method' | 'body'>
) {
    return envelope<T>(http.post<ApiEnvelope<T>>(path, body, options));
}

/** 领域 PUT 的轻量封装。 */
export function put<T>(
    http: EnterHttpClient,
    path: string,
    body?: unknown,
    options?: Omit<HttpRequestOptions, 'method' | 'body'>
) {
    return envelope<T>(http.put<ApiEnvelope<T>>(path, body, options));
}

/** 领域 PATCH 的轻量封装。 */
export function patch<T>(
    http: EnterHttpClient,
    path: string,
    body?: unknown,
    options?: Omit<HttpRequestOptions, 'method' | 'body'>
) {
    return envelope<T>(http.patch<ApiEnvelope<T>>(path, body, options));
}

/** 领域 DELETE 的轻量封装，DELETE 的 query 仍然遵循统一清理规则。 */
export function del<T>(
    http: EnterHttpClient,
    path: string,
    query?: object,
    options?: Omit<HttpRequestOptions, 'method' | 'query'>
) {
    return envelope<T>(
        http.request<ApiEnvelope<T>>(path, {
            ...options,
            method: 'DELETE',
            query: cleanQuery(query) as QueryParams | undefined,
        })
    );
}
